import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Service, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import {
  HANDLE_HTTP_ERROR_LOCALLY,
  SKIP_AUTH_TOKEN,
  SKIP_SUCCESS_ALERT,
} from '../../../core/http/http-context-tokens';
import { PedidoSicPublicoResponse } from '../models/pedido-sic-acompanhamento.model';
import {
  MENSAGEM_CAPTCHA_RECUSADO,
  RespostaCaptcha,
  cabecalhosCaptcha,
  ehRecusaDoCaptcha,
} from './captcha-consulta.service';

const PUBLICO = `${environment.apiUrl}/sic/publico/pedido`;

/** O erro fica local: cada tela mostra a própria falha, sem o modal do interceptor. */
export const ERRO_LOCAL_SIC = () => new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true);

/** Erro local e sem o alerta automático de sucesso: a ação parte de um `<dialog>` ou de um card. */
export const SILENCIOSO_SIC = () => ERRO_LOCAL_SIC().set(SKIP_SUCCESS_ALERT, true);

/**
 * A rota pelo protocolo — aparado, em maiúsculas e codificado, como o back-end o compara. O
 * protocolo é da ROTA: nunca vai no corpo.
 */
export function rotaPedidoSic(base: string, protocolo: string): string {
  return `${base}/${encodeURIComponent(protocolo.trim().toUpperCase())}`;
}

export const LIMITE_REQUISICOES = 'Muitas tentativas em pouco tempo. Aguarde alguns instantes e tente novamente.';

/**
 * A superfície **pública** do pedido do e-SIC: a consulta por protocolo de `/acompanhamento` e a
 * resposta ao complemento. O protocolo é a credencial — o pedido pode ser anônimo —, e por isso
 * as duas chamadas saem **sem** o token: a sessão não muda nada do lado do servidor, e o CPF da
 * tela nunca vai junto.
 */
@Service()
export class PedidoSicAcompanhamentoService {
  private readonly http = inject(HttpClient);

  /** A consulta por protocolo leva o captcha da tela nos cabeçalhos (change captcha-consulta-protocolo). */
  consultar(protocolo: string, captcha: RespostaCaptcha): Observable<PedidoSicPublicoResponse> {
    return this.http
      .get<ResponseApi<PedidoSicPublicoResponse>>(rotaPedidoSic(PUBLICO, protocolo), {
        context: ERRO_LOCAL_SIC().set(SKIP_AUTH_TOKEN, true),
        headers: cabecalhosCaptcha(captcha),
      })
      .pipe(map((resposta) => resposta.data));
  }

  /**
   * A resposta ao complemento em `multipart/form-data`. A resposta é a consulta pública já
   * atualizada; o erro fica no card. Sem `Content-Type` manual — o boundary é do navegador.
   */
  enviarComplemento(protocolo: string, texto: string, arquivos: readonly File[]): Observable<PedidoSicPublicoResponse> {
    return this.http
      .post<ResponseApi<PedidoSicPublicoResponse>>(
        `${rotaPedidoSic(PUBLICO, protocolo)}/complemento`,
        montarFormDataComplementoSic(texto, arquivos),
        { context: SILENCIOSO_SIC().set(SKIP_AUTH_TOKEN, true) },
      )
      .pipe(map((resposta) => resposta.data));
  }
}

/** O `multipart/form-data` da resposta ao complemento: o texto aparado e um `Anexos` por arquivo. */
export function montarFormDataComplementoSic(texto: string, arquivos: readonly File[]): FormData {
  const corpo = new FormData();

  corpo.append('Texto', texto.trim());

  for (const arquivo of arquivos) {
    corpo.append('Anexos', arquivo, arquivo.name);
  }

  return corpo;
}

/** O que a tela de acompanhamento decide quando a consulta falha. */
export interface ErroConsultaSic {
  tipo: 'nao-encontrado' | 'limite' | 'indisponivel' | 'captcha';
  mensagem: string;
}

/**
 * Traduz o erro da consulta: o `404` é o "Protocolo Não Encontrado" de sempre; o `422` marcado pela
 * API, a recusa do captcha; o `429`, o aviso de limite; o resto, a indisponibilidade — **sem** a
 * mensagem da API, que num `5xx` traria o texto do banco.
 */
export function mapearErroConsultaSic(erro: unknown): ErroConsultaSic {
  if (ehRecusaDoCaptcha(erro)) {
    return { tipo: 'captcha', mensagem: MENSAGEM_CAPTCHA_RECUSADO };
  }

  const status = erro instanceof HttpErrorResponse ? erro.status : 0;

  switch (status) {
    case 404:
      return { tipo: 'nao-encontrado', mensagem: 'Não encontramos pedido com este protocolo.' };

    case 429:
      return { tipo: 'limite', mensagem: LIMITE_REQUISICOES };

    default:
      return { tipo: 'indisponivel', mensagem: 'Não foi possível consultar o protocolo agora. Tente novamente em instantes.' };
  }
}

/** O que o card de complemento mostra quando o envio falha. */
export interface ErroComplementoSic {
  mensagem: string;
  detalhes: readonly string[];
}

/** A `message` e os `errors` do envelope, sem repetir a mensagem nos detalhes. */
export function lerEnvelopeErro(erro: unknown): { status: number; mensagem: string; detalhes: string[] } {
  const resposta = erro instanceof HttpErrorResponse ? erro : null;
  const corpo = (resposta?.error ?? null) as Partial<ResponseApi<unknown>> | null;

  const mensagem = typeof corpo?.message === 'string' ? corpo.message.trim() : '';

  const detalhes = (Array.isArray(corpo?.errors) ? corpo.errors : [])
    .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    .map((item) => item.trim())
    .filter((item) => item !== mensagem);

  return { status: resposta?.status ?? 0, mensagem, detalhes };
}

/**
 * Traduz o erro da resposta ao complemento. A `message` da API vence no `400`, `404` e `422`: é
 * ela que diz qual arquivo foi recusado ou que o prazo do cidadão terminou, com a data.
 */
export function mapearErroComplementoSic(erro: unknown): ErroComplementoSic {
  const { status, mensagem, detalhes } = lerEnvelopeErro(erro);

  switch (status) {
    case 400:
      return { mensagem: mensagem || 'Não foi possível enviar o complemento com estes dados.', detalhes };

    case 404:
      return { mensagem: mensagem || 'Não encontramos pedido com este protocolo.', detalhes: [] };

    case 413:
      return { mensagem: 'Os arquivos passam do tamanho total aceito. Remova algum anexo e tente de novo.', detalhes: [] };

    case 422:
      return { mensagem: mensagem || 'Este pedido não está aguardando complemento.', detalhes: [] };

    case 429:
      return { mensagem: LIMITE_REQUISICOES, detalhes: [] };

    default:
      return { mensagem: 'Não foi possível enviar o complemento agora. Tente novamente em instantes.', detalhes: [] };
  }
}
